import React, { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Colors, Spacing } from 'constants/theme';

type SelectOptions = {
  label: string;
  value: string;
};

type SelectFieldProps = JSX.IntrinsicElements['select'] & {
  errorMessage?: string;
  hasMask?: boolean;
  icon?: React.FC;
  label?: string;
  note?: string;
  options: SelectOptions[];
};

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      errorMessage,
      icon: Icon,
      id,
      label,
      note,
      options,
      placeholder,
      value,
      ...props
    },
    ref
  ) => {
    const [isEmpty, setIsEmpty] = useState(!value && !props.defaultValue);
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus: React.FocusEventHandler<HTMLSelectElement> = (e) => {
      props.onFocus?.(e);
      setIsFocused(true);
    };

    const handleBlur: React.FocusEventHandler<HTMLSelectElement> = (e) => {
      props.onBlur?.(e);
      setIsFocused(false);
    };

    const handleOnChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
      props.onChange?.(e);
      if (e.target.value.length) {
        setIsEmpty(false);
      } else {
        setIsEmpty(true);
      }
    };

    return (
      <Container>
        <Label>
          {label ? (
            <SelectLabel data-testid="label" hasIcon={!!Icon} isEmpty={isEmpty}>
              {label}
            </SelectLabel>
          ) : null}
          {Icon ? (
            <IconWrapper>
              <Icon />
            </IconWrapper>
          ) : null}
          <Select
            {...(props as any)}
            defaultValue={props.defaultValue || ''}
            hasError={!!errorMessage}
            hasIcon={!!Icon}
            hasLabel={!!label}
            id={id}
            isEmpty={isEmpty}
            isFocused={isFocused}
            onBlur={handleBlur}
            onChange={handleOnChange}
            onFocus={handleFocus}
            ref={ref}
            value={value}
          >
            {placeholder ? (
              <option value="">{placeholder}</option>
            ) : (
              <option value="">Select</option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Label>
        {errorMessage ? (
          <ErrorMessage data-testid="error">{errorMessage}</ErrorMessage>
        ) : null}
        {note ? <Note data-testid="note">{note}</Note> : null}
      </Container>
    );
  }
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: block;
  position: relative;
  ::after {
    content: '';
    position: absolute;
    width: 1px;
    background-color: ${Colors.grey3};
    height: 34px;
    right: 38px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const IconWrapper = styled.span`
  color: ${Colors.grey4};
  display: flex;
  font-size: 20px;
  line-height: 20px;
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
`;

const SelectLabel = styled.span<{
  hasIcon: boolean;
  isEmpty: boolean;
}>`
  color: ${Colors.grey5};
  font-family: 'EuclidCircularB', sans-serif;
  left: ${(props) => (props.hasIcon ? '44px' : Spacing.s)};
  pointer-events: none;
  position: absolute;
  transition: all 0.3s ease-in-out;
  ${(props) =>
    props.isEmpty
      ? css`
          font-size: 16px;
          line-height: 24px;
          top: 50%;
          transform: translateY(-50%);
        `
      : css`
          font-size: 10px;
          line-height: 10px;
          padding-top: 5px;
          text-transform: uppercase;
          top: 0;
        `}
`;

const Select = styled.select<{
  hasError: boolean;
  hasIcon: boolean;
  hasLabel: boolean;
  isEmpty: boolean;
  isFocused: boolean;
}>`
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg fill='%234F7F71' height='7' viewBox='0 0 12 7' width='12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m11.3.299609c-.3866-.386599-1.0134-.3865988-1.4.000001l-3.9 3.9-3.9-3.900001c-.3866-.3865992-1.0134-.3865991-1.4 0-.386599.3866-.386599 1.013401 0 1.400001l5.3 5.3 5.3-5.3c.3866-.3866.3866-1.013401 0-1.400001z' /%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  border-radius: 4px;
  border: 1px solid
    ${(props) =>
      props.isFocused
        ? Colors.sage4
        : props.hasError
        ? Colors.poppy6
        : Colors.grey5};
  box-sizing: border-box;
  color: ${(props) => (props.isEmpty ? 'transparent' : Colors.grey6)};
  font-family: 'EuclidCircularB', sans-serif;
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  outline: none;
  text-overflow: ellipsis;
  transition: border-color 0.3s ease-in-out;
  width: 100%;
  ::-ms-expand {
    display: none;
  }
  &:hover {
    border-color: ${Colors.sage4};
  }
  ${(props) =>
    props.hasLabel
      ? css`
          padding: 15px 44px 5px ${props.hasIcon ? '44px' : '10px'};
        `
      : css`
          padding: 10px 44px 10px ${props.hasIcon ? '44px' : '10px'};
        `}
`;

const ErrorMessage = styled.span`
  font-family: EuclidCircularB;
  font-size: 12px;
  line-height: 16px;
  color: ${Colors.poppy6};
  margin-top: 5px;
`;

const Note = styled.div`
  font-family: EuclidCircularB;
  font-size: 12px;
  line-height: 16px;
  color: ${Colors.grey5};
  margin-top: 5px;
`;
